import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { ONBOARDING_PATHS } from './constants/paths';
import { SALES_FEATURES } from './constants/sales-copy';
import { OnboardingLayout } from './components/OnboardingLayout';
import { SalesHeroCarousel } from './components/SalesHeroCarousel';
import { FaqAccordion } from './components/FaqAccordion';
import { useSubscriptionPricing } from '../../hooks/useSubscription';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';

const LegacySalesPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { trackUpsellStarted } = useOnboardingTracker();
  const {
    data: pricing,
    isLoading: isPricingLoading,
    isError: isPricingError,
  } = useSubscriptionPricing();

  let pricingLabel: string | null = null;
  if (isPricingLoading) pricingLabel = 'Loading pricing…';
  else if (isPricingError) pricingLabel = 'Pricing unavailable';
  else if (pricing) pricingLabel = `From ${pricing.monthlyFormatted} monthly / per site`;

  return (
    <OnboardingLayout transparentBg>
      <div className="vtwo:text-center vtwo:mb-12">
        <h1 className="vtwo:text-4xl vtwo:font-bold vtwo:mb-2">
          Take your online presence to the next level
        </h1>
        <p className="vtwo:text-base vtwo:text-gray-700">Convert visitors to paying customers</p>
      </div>

      <div className="vtwo:flex vtwo:flex-col vtwo:lg:flex-row vtwo:items-stretch vtwo:bg-white vtwo:rounded-2xl vtwo:overflow-hidden">
        <div className="vtwo:lg:w-9/20 vtwo:min-w-0 vtwo:flex vtwo:flex-col vtwo:gap-6 vtwo:p-8">
          <div>
            <h2 className="vtwo:text-3xl vtwo:font-bold vtwo:mb-2">Smart website</h2>
            <p className="vtwo:text-sm vtwo:text-black vtwo:mb-1">Launch your site in minutes.</p>
            <p className="vtwo:text-sm vtwo:text-black">
              Built in website builder - no coding needed
            </p>
          </div>

          <div className="vtwo:bg-stone-100 vtwo:rounded-xl vtwo:p-4 vtwo:text-center">
            <p className="vtwo:text-sm vtwo:text-black vtwo:mb-3">
              Start now for free and see what your future website could look like.
            </p>
            <Button
              asChild
              size="xl"
              className="vtwo:w-full vtwo:bg-featured-cta vtwo:hover:bg-featured-cta-hover vtwo:rounded-full vtwo:no-underline"
            >
              <Link
                to={{
                  pathname: ONBOARDING_PATHS.steps,
                  search: searchParams.toString(),
                }}
                onClick={trackUpsellStarted}
              >
                Start building my website
              </Link>
            </Button>
          </div>

          <div className="vtwo:flex vtwo:flex-col vtwo:gap-2">
            {SALES_FEATURES.map((feature) => (
              <div key={feature} className="vtwo:flex vtwo:items-center vtwo:gap-2">
                <Check className="vtwo:size-4 vtwo:text-primary" />
                <span className="vtwo:text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <Separator />

          <div className="vtwo:text-center">
            <p className="vtwo:text-sm vtwo:font-bold vtwo:text-gray-800">{pricingLabel}</p>
          </div>
        </div>

        <div className="vtwo:lg:w-11/20 vtwo:min-w-0">
          <SalesHeroCarousel />
        </div>
      </div>

      <div className="vtwo:mt-12">
        <FaqAccordion />
      </div>
    </OnboardingLayout>
  );
};

export default LegacySalesPage;
